import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, filter, finalize, map, of, shareReplay, tap } from "rxjs";
import { Color } from "src/app/models/color.model";
import { ColorStore } from "../akita/colors.store";
import { environment } from "src/environments/enviroment.prod";
import { LocalProductLQ } from "src/app/models/localProductLQ.model";
import { Router, NavigationEnd } from "@angular/router";
import { ColorsQuery } from "../akita/colors.query";
import { createCarouselPreview } from "./carousel-capture.util";
import { PreviewLoadingService } from "../preview-loading/preview-loading.service";
import { ConfiguratorProgressService } from "../configurator-progress/configurator-progress.service";

@Injectable({
  providedIn: 'root'
})
export class NavBarService {
  private ColorCache:  Color[]  = [];
  private _toggleNavBar = new BehaviorSubject<boolean>(false);
  private openedSubject = new BehaviorSubject<boolean>(false);
  opened$ = this.openedSubject.asObservable();
  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable()
  private _toggleSummaryBvBar = new BehaviorSubject<boolean>(false);
  private Indexloading = new BehaviorSubject<boolean>(false);
  Indexloading$ = this.Indexloading.asObservable()
  private lastNext = new BehaviorSubject<boolean>(false);
  lastNext$ = this.Indexloading.asObservable()
  private openedSummarySubject = new BehaviorSubject<boolean>(false);
  openedSummary$ = this.openedSummarySubject.asObservable();
  private carouselElement?: HTMLElement;
  // Theme request de-duplication (ProductComponent and NavbarComponent ask for
  // the same theme at the same moment). See getThemeName()/fetchTheme().
  private static readonly THEME_NAME_TTL_MS = 60_000;
  private themeName$?: Observable<string>;
  private themeNameAt = 0;
  private readonly themeRequests = new Map<string, Observable<any>>();
  private previousUrl: string | null = null;
  private currentUrl: string | null = null;

  constructor(private http: HttpClient, private colorStore: ColorStore,  private router: Router,
    private colorsQuery: ColorsQuery,
    private previewLoading: PreviewLoadingService,
    private progress: ConfiguratorProgressService,
    ) {
      this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
        console.log('previous url', this.previousUrl)
        console.log('cuurent url', this.currentUrl)
        console.log('event', event)
      });


    }

  setLoading(loading: boolean){
    this.loadingSubject.next(loading);
  }

  setIndexLoading(loading: boolean){
    this.Indexloading.next(loading);
  }


  setLastNext(nextIndex: boolean){
    this.lastNext.next(nextIndex);
    // The summary drawer is gone; the choices rail owns checkout now.
    this.progress.requestCheckout();
  }

  setCarouselElement(element: HTMLElement): void {
    this.carouselElement = element;
  }

  public getPreviousUrl(): string | null {
    return this.previousUrl;
  }

  public navigateBack(final_product_id:number): void {
    if (!this.previousUrl) {
      this.router.navigate(['/cart']);
      return;
    }

    // Backing out of order details used to return to the configurator. It is
    // reset by then — the configuration has been committed to the cart — so
    // that would land on an empty screen. The cart is where the work is.
    if (this.previousUrl.includes('order-details')) {
      this.router.navigate(['/cart']);
      return;
    }

    this.router.navigateByUrl(this.previousUrl);
  }

  async captureCarouselImage(): Promise<string> {
    const maxAttempts = 3; // first try + 2 retries
    let lastError: unknown;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (!this.carouselElement?.isConnected) {
        localStorage.removeItem('carouselImage');
        throw new Error('Carousel is not ready.');
      }

      try {
        // Let pending layer preloads settle first (bounded) so the capture shows the final picture.
        await this.previewLoading.whenIdle(15000);
        const image = await createCarouselPreview(this.carouselElement);
        localStorage.setItem('carouselImage', image);
        return image;
      } catch (error) {
        lastError = error;
        if (attempt < maxAttempts) {
          await new Promise<void>(resolve => setTimeout(resolve, 500));
        }
      }
    }

    localStorage.removeItem('carouselImage');
    throw lastError;
  }


  get toggleNavBar() {
    return this._toggleNavBar.asObservable();
  }

  get toggleSummaryBar(){
    return this._toggleSummaryBvBar.asObservable();

  }

  setToggleSummaryBar(value: boolean) {
    this.openedSummarySubject.next(value);
  }

  setToggleNavBar(value: boolean) {

    this.openedSubject.next(value);
    this._toggleNavBar.next(value);
  }

  /**
   * The loader is raised on every call (as before). The HTTP request itself is
   * shared: concurrent callers, and callers within THEME_NAME_TTL_MS, reuse one
   * request. The theme name is admin-editable server state, so it is not cached
   * for the whole session; a failed request is dropped so the next call retries.
   */
  getThemeName(): Observable<string> {
    this.setLoading(true);

    const fresh = this.themeName$ && Date.now() - this.themeNameAt < NavBarService.THEME_NAME_TTL_MS;
    if (!fresh) {
      const request$: Observable<string> = this.http
        .get<{ theme_name: string }>(`${environment.apiLink}/getThemeName`)
        .pipe(
          map(response => response.theme_name),
          tap({
            error: () => {
              if (this.themeName$ === request$) { this.themeName$ = undefined; }
            },
          }),
          shareReplay(1)
        );
      this.themeName$ = request$;
      this.themeNameAt = Date.now();
    }
    return this.themeName$!;
  }

  // The configurator screen (/home/product/:id) keeps the loader up until the
  // navbar finishes wiring tabs. Every other screen (mode, lines, home,
  // category step, checkout, login, order-details, cart) should clear it once
  // the theme is resolved.
  //
  // /mode and /cart were added after this allowlist and initially missed it:
  // <app-navbar> is on both, getThemeName() unconditionally sets the loader,
  // and with neither route matching here fetchTheme() never cleared it —
  // the loading overlay stayed up forever on those screens.
  private shouldClearLoadingForCurrentRoute(): boolean {
    const url = this.router.url;
    return url.includes('/checkout')
      || url.includes('/lines')
      || (url.includes('/home') && !url.includes('/product'))
      || url.includes('/login')
      || url.includes('/order-details')
      || url.includes('/mode')
      || url.includes('/cart');
  }

  fetchTheme(theme: string) {
    // Check if the colors for the theme are already in the store
    const cachedColors = this.colorsQuery.getAll({
      filterBy: color => color.name === theme,
    });

    if (cachedColors.length > 0) {
      // If cached data exists, still clear the loader on non-configurator routes.
      if (this.shouldClearLoadingForCurrentRoute()) {
        this.setLoading(false);
      }
      return of(cachedColors);
    } else {
      // No cached data: fetch from the server. Callers that arrive while the
      // request is in flight share it, so the store write and loader clearing
      // below run once per real request. Once it settles (success or error) the
      // entry is dropped: later callers are served by the store check above, or
      // retry after an error.
      let request$ = this.themeRequests.get(theme);
      if (!request$) {
        request$ = this.http.get<{ data: any }>(`${environment.apiLink}/colors/theme/${theme}`).pipe(
          map(response => response.data),
          tap(colors => {
            // Store the fetched colors in the Akita store
            this.colorStore.set(colors);
            if (this.shouldClearLoadingForCurrentRoute()) {
              this.setLoading(false);
            }
          }),
          finalize(() => this.themeRequests.delete(theme)),
          shareReplay(1)
        );
        this.themeRequests.set(theme, request$);
      }
      return request$;
    }
  }


  fetchColors() {
    if (this.ColorCache.length > 0) {
      // If colors are already cached, return the cached colors
      return of(this.ColorCache);
    }

    this.colorStore.setLoading(true);

    return this.http.get<{ data: Color[] }>(`${environment.apiLink}/colors`).pipe(
      map((response: { data: Color[] }) => response.data),
      tap((newColors) => {
        console.log(newColors);

        // Append new colors to the existing ones
        this.ColorCache = [...this.ColorCache, ...newColors];

        // Update the color store with the combined colors
        this.colorStore.set(this.ColorCache);
        this.colorStore.setLoading(false);
      })
    );
  }

  fetchLQ(final_product_id:number): Observable<LocalProductLQ[]>{
    return this.http.get<{data:LocalProductLQ[]}>(`${environment.apiLink}/fetchLQ?final_product_id=${final_product_id}`).pipe(
      map(response => response.data),
      tap(response => {
        console.log(response);
      })
    );
  }



}
